/**
 * Custom React components for the unified/rehype-react pipeline.
 * Every standard markdown-generated HTML element is intercepted here
 * and styled with Forge Design System V2 tokens.
 */

import React from 'react'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type P = Record<string, any>

/** Recursively extract plain text from React children for stable anchor IDs. */
function extractReactText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractReactText).join('')
  if (React.isValidElement(node)) {
    return extractReactText((node.props as { children?: React.ReactNode }).children)
  }
  return ''
}

/** Derive a stable DOM id from heading text for TOC anchor links. */
function headingId(children: React.ReactNode): string {
  return extractReactText(children)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
}

// ─── Headings ─────────────────────────────────────────────────────────────────

const H1 = ({ children, ...p }: P) => (
  <h1 id={headingId(children)} className="font-display text-3xl font-bold text-forge-primary mt-10 mb-4 pb-3 border-b border-forge-border leading-tight" {...p}>
    {children}
  </h1>
)

const H2 = ({ children, ...p }: P) => (
  <h2 id={headingId(children)} className="font-display text-2xl font-semibold text-forge-primary mt-8 mb-3 pb-2 border-b border-forge-border/50 leading-tight" {...p}>
    {children}
  </h2>
)

const H3 = ({ children, ...p }: P) => (
  <h3 id={headingId(children)} className="font-display text-xl font-semibold text-forge-primary mt-6 mb-2 leading-tight" {...p}>
    {children}
  </h3>
)

const H4 = ({ children, ...p }: P) => (
  <h4 id={headingId(children)} className="font-display text-lg font-semibold text-forge-gold mt-5 mb-2" {...p}>
    {children}
  </h4>
)

const H5 = ({ children, ...p }: P) => (
  <h5 id={headingId(children)} className="font-display text-base font-medium text-forge-amber mt-4 mb-1" {...p}>
    {children}
  </h5>
)

const H6 = ({ children, ...p }: P) => (
  <h6 id={headingId(children)} className="text-sm font-semibold text-forge-ash uppercase tracking-wider mt-4 mb-1" {...p}>
    {children}
  </h6>
)

// ─── Inline Text ──────────────────────────────────────────────────────────────

const P = ({ children, ...p }: P) => (
  <p className="text-forge-secondary leading-7 mb-4 text-[0.9375rem]" {...p}>
    {children}
  </p>
)

const A = ({ children, href, ...p }: P) => (
  <a
    href={href as string}
    target={href?.startsWith('http') ? '_blank' : undefined}
    rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
    className="text-forge-gold hover:text-forge-amber underline underline-offset-2 transition-colors"
    {...p}
  >
    {children}
  </a>
)

const Strong = ({ children, ...p }: P) => (
  <strong className="font-semibold text-forge-primary" {...p}>{children}</strong>
)

const Em = ({ children, ...p }: P) => (
  <em className="italic text-forge-secondary" {...p}>{children}</em>
)

const Del = ({ children, ...p }: P) => (
  <del className="line-through text-forge-ash" {...p}>{children}</del>
)

// ─── Code ─────────────────────────────────────────────────────────────────────

const InlineCode = ({ children, ...p }: P) => (
  <code
    className="font-mono text-forge-gold text-[0.85em] bg-forge-basalt px-1.5 py-0.5 rounded border border-forge-border/50"
    {...p}
  >
    {children}
  </code>
)

const Pre = ({ children, ...p }: P) => (
  <pre
    className="bg-forge-obsidian rounded-lg border border-forge-border overflow-x-auto my-5 text-sm"
    {...p}
  >
    {children}
  </pre>
)

// Code inside Pre — hljs classes are injected by rehype-highlight; we add font fallback
const Code = ({ children, className, ...p }: P) => (
  <code
    className={[
      'font-mono text-sm',
      className as string | undefined,
    ].filter(Boolean).join(' ')}
    {...p}
  >
    {children}
  </code>
)

// ─── Blockquote ───────────────────────────────────────────────────────────────

const Blockquote = ({ children, ...p }: P) => (
  <blockquote
    className="border-l-4 border-forge-ember bg-forge-basalt/50 pl-4 pr-3 py-3 my-5 rounded-r-lg italic text-forge-secondary"
    {...p}
  >
    {children}
  </blockquote>
)

// ─── Lists ────────────────────────────────────────────────────────────────────

const Ul = ({ children, ...p }: P) => (
  <ul className="list-disc list-outside pl-6 mb-4 space-y-1 text-forge-secondary text-[0.9375rem]" {...p}>
    {children}
  </ul>
)

const Ol = ({ children, ...p }: P) => (
  <ol className="list-decimal list-outside pl-6 mb-4 space-y-1 text-forge-secondary text-[0.9375rem]" {...p}>
    {children}
  </ol>
)

const Li = ({ children, ...p }: P) => (
  <li className="leading-7 marker:text-forge-ember" {...p}>{children}</li>
)

// ─── Tables ───────────────────────────────────────────────────────────────────

const Table = ({ children, ...p }: P) => (
  <div className="overflow-x-auto my-6 rounded-lg border border-forge-border">
    <table className="w-full text-sm text-left" {...p}>
      {children}
    </table>
  </div>
)

const Thead = ({ children, ...p }: P) => (
  <thead className="bg-forge-slate border-b border-forge-border" {...p}>
    {children}
  </thead>
)

const Tbody = ({ children, ...p }: P) => (
  <tbody className="divide-y divide-forge-border/50" {...p}>
    {children}
  </tbody>
)

const Tr = ({ children, ...p }: P) => (
  <tr className="hover:bg-forge-basalt/30 transition-colors" {...p}>
    {children}
  </tr>
)

const Th = ({ children, ...p }: P) => (
  <th className="px-4 py-2.5 font-semibold text-forge-gold text-xs uppercase tracking-wider" {...p}>
    {children}
  </th>
)

const Td = ({ children, ...p }: P) => (
  <td className="px-4 py-2.5 text-forge-secondary" {...p}>
    {children}
  </td>
)

// ─── Misc ─────────────────────────────────────────────────────────────────────

const Hr = (p: P) => (
  <hr className="border-forge-border my-8" {...p} />
)

const Img = ({ src, alt, ...p }: P) => (
  <img
    src={src as string}
    alt={alt as string | undefined}
    className="max-w-full rounded-lg border border-forge-border my-4 mx-auto block"
    loading="lazy"
    {...p}
  />
)

// ─── Export ───────────────────────────────────────────────────────────────────

export const mdComponents = {
  h1: H1, h2: H2, h3: H3, h4: H4, h5: H5, h6: H6,
  p:  P,
  a:  A,
  strong: Strong,
  em:     Em,
  del:    Del,
  code:   Code,
  pre:    Pre,
  inlinecode: InlineCode,
  blockquote: Blockquote,
  ul: Ul, ol: Ol, li: Li,
  table: Table, thead: Thead, tbody: Tbody, tr: Tr, th: Th, td: Td,
  hr:  Hr,
  img: Img,
}
